import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AgeCalculator1 = () => {
  const [age, setAge] = useState('');
  const [planet, setPlanet] = useState('earth');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleAgeChange = (e) => {
    setAge(e.target.value);
    setError('');
  };

  const handlePlanetChange = (e) => {
    setPlanet(e.target.value);
  };

  const calculateAge = () => {
    if (!age) {
      setError('Please enter your age.');
      return;
    }

    const ageNumber = parseFloat(age);

    if (isNaN(ageNumber) || ageNumber <= 0) {
      setError('Please enter a valid age.');
      return;
    }

    const earthYearInSeconds = 31557600; // seconds
    const planetYearsInSeconds = {
      earth: earthYearInSeconds,
      mercury: 7600530.24,
      venus: 19414147.2,
      mars: 59354294.4,
      jupiter: 374335776,
      saturn: 929292960,
      uranus: 2651370240,
      neptune: 5200418592,
      moon: 23605920, // Moon's orbital period around Earth
      pluto: 7816432320 // Pluto's orbital period around the Sun
    };

    const ageInSeconds = ageNumber * earthYearInSeconds;
    const ageOnPlanet = ageInSeconds / planetYearsInSeconds[planet];

    setResult(ageOnPlanet.toFixed(2));

    // Update chart data
    setChartData({
      labels: ['Earth', planet.charAt(0).toUpperCase() + planet.slice(1)],
      datasets: [
        {
          label: 'Age Comparison',
          data: [ageNumber, ageOnPlanet],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateAge();
  };

  return (
    <>
      <Helmet>
        <title>Age on Other Planets Calculator | BioLab Tools</title>
        <meta name="description" content="Calculate your age on different planets with ease. Compare your age on Earth, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Moon, and Pluto." />
        <meta name="keywords" content="age calculator, age on planets, planet age comparison, moon age, pluto age" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Age on Other Planets Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Calculate your age on different planets with ease. Compare your age on Earth, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Moon, and Pluto.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Age</label>
                <input
                  type="number"
                  value={age}
                  onChange={handleAgeChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter your age"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Planet</label>
                <select
                  name="planet"
                  value={planet}
                  onChange={handlePlanetChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="earth">Earth</option>
                  <option value="mercury">Mercury</option>
                  <option value="venus">Venus</option>
                  <option value="mars">Mars</option>
                  <option value="jupiter">Jupiter</option>
                  <option value="saturn">Saturn</option>
                  <option value="uranus">Uranus</option>
                  <option value="neptune">Neptune</option>
                  <option value="moon">Moon</option>
                  <option value="pluto">Pluto</option>
                </select>
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Calculate
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {result && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">Your age on {planet.charAt(0).toUpperCase() + planet.slice(1)}: {result}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Age' }
                            },
                            x: { title: { display: true, text: 'Planet' } }
                          },
                          plugins: { title: { display: true, text: 'Age Comparison' } }
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What types of planets are supported?</h3>
                <p className="text-gray-600">
                  The Age Calculator supports all major planets in our solar system: Earth, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Moon, and Pluto.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How accurate are the calculations?</h3>
                <p className="text-gray-600">
                  The calculations are highly accurate, using standard astronomical data for the orbital periods of each planet.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for educational purposes?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is perfect for educational purposes, helping students understand and compare the relative ages on different planets.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Age Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Your Age</h3>
                <p className="text-gray-600">
                  Input your age in years.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Select Planet</h3>
                <p className="text-gray-600">
                  Choose the planet you want to compare your age to.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Calculate</h3>
                <p className="text-gray-600">
                  Click the "Calculate" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 4: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the age comparison.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AgeCalculator1;